"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";

const PRODUCT_PREFIXES = [
  "/account",
  "/admin",
  "/checks",
  "/history",
  "/new",
  "/place",
  "/places",
  "/result",
  "/tasks",
];

function isProductPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return PRODUCT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * `/` is the dashboard for signed-in users and a server-side redirect
 * to rekkd.io for everyone else. Read the auth hint set by the root
 * layout's inline script so we can pick the right footer without a flash.
 */
function useAuthHint(): boolean {
  const [authed, setAuthed] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.getAttribute("data-auth-hint") === "1";
  });

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setAuthed(el.getAttribute("data-auth-hint") === "1");
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, {
      attributes: true,
      attributeFilter: ["data-auth-hint"],
    });
    return () => observer.disconnect();
  }, []);

  return authed;
}

export default function SiteFooter({
  marketing,
  product,
}: {
  marketing: ReactNode;
  product: ReactNode;
}) {
  const pathname = usePathname();
  const authed = useAuthHint();
  const onDashboard = pathname === "/";
  return isProductPath(pathname) || (onDashboard && authed)
    ? product
    : marketing;
}
